import { HoursField } from "@superadmin/components/HoursField";
import { PageShell } from "@superadmin/components/PageShell";
import { formatEpoch, formatMs } from "@superadmin/utils/format";
import { orpc } from "@superadmin/utils/orpc.tanstack.client";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { LoadingSpinner } from "@voicely/ui-mui/components/LoadingSpinner";
import { Chip } from "@voicely/ui-mui/data-display/Chip";
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from "@voicely/ui-mui/data-display/Table";
import { Typography } from "@voicely/ui-mui/data-display/Typography";
import { Alert } from "@voicely/ui-mui/feedback/Alert";
import { MenuItem } from "@voicely/ui-mui/form/MenuItem";
import { TextField } from "@voicely/ui-mui/form/TextField";
import { Paper } from "@voicely/ui-mui/layout/Paper";
import { Stack } from "@voicely/ui-mui/layout/Stack";
import { useUrlState } from "@voicely/web-shared/use_url_state";
import { VOICE_SESSION_STATUS_ENUM } from "@voicely/zod-schemas/enums.zod";
import { useNavigate } from "react-router";
import { z } from "zod";

const hoursSchema = z.coerce.number().int().min(1).max(168);
const stringSchema = z.string();

// The enum itself, so a status this filter offers is one a row can carry.
// A hand-written list once offered "cancelled", which no session has ever
// been, and the server accepted it as free text — a filter that silently
// matched nothing.
const STATUSES = ["", ...VOICE_SESSION_STATUS_ENUM] as const;
const statusSchema = z.enum(STATUSES);

/**
 * Cross-workspace session index — the page that answers "which call" when a
 * report arrives without an id. Rows link to the full dump.
 */
export const SessionsPage = () => {
	const navigate = useNavigate();
	const [hours, setHours] = useUrlState("hours", hoursSchema, 24);
	const [teamId, setTeamId] = useUrlState("team", stringSchema, "");
	const [status, setStatus] = useUrlState("status", statusSchema, "");

	const sessionsQuery = useQuery(
		orpc.superAdmin.listSessions.queryOptions({
			input: {
				withinHours: hours,
				limit: 100,
				teamId: teamId || undefined,
				sessionStatus: status || undefined,
			},
			placeholderData: keepPreviousData,
		}),
	);

	const teamsQuery = useQuery(orpc.superAdmin.listTeams.queryOptions());
	const teamName = (id: string | null) =>
		teamsQuery.data?.find((team) => team.id === id)?.name ?? id ?? "—";

	const toolbar = (
		<Stack direction="row" spacing={1.5} sx={{ flexWrap: "wrap", gap: 1 }}>
			<HoursField value={hours} onChange={setHours} />
			<TextField
				select
				size="small"
				label="Workspace"
				value={teamId}
				onChange={(event) => setTeamId(event.target.value)}
				sx={{ minWidth: 200 }}
			>
				<MenuItem value="">All workspaces</MenuItem>
				{(teamsQuery.data ?? []).map((team) => (
					<MenuItem key={team.id} value={team.id}>
						{team.name}
					</MenuItem>
				))}
			</TextField>
			<TextField
				select
				size="small"
				label="Status"
				value={status}
				onChange={(event) => setStatus(statusSchema.parse(event.target.value))}
				sx={{ minWidth: 140 }}
			>
				{STATUSES.map((value) => (
					<MenuItem key={value} value={value}>
						{value === "" ? "Any status" : value}
					</MenuItem>
				))}
			</TextField>
		</Stack>
	);

	if (sessionsQuery.isPending) {
		return (
			<PageShell title="Sessions" toolbar={toolbar}>
				<LoadingSpinner />
			</PageShell>
		);
	}

	if (sessionsQuery.error) {
		return (
			<PageShell title="Sessions" toolbar={toolbar}>
				<Alert severity="error">{sessionsQuery.error.message}</Alert>
			</PageShell>
		);
	}

	const sessions = sessionsQuery.data;

	return (
		<PageShell
			title="Sessions"
			subtitle="Newest first, across every workspace (100 max)"
			toolbar={toolbar}
			maxWidth={false}
		>
			{sessions.length === 0 ? (
				<Typography color="text.secondary">Nothing in this window.</Typography>
			) : (
				<TableContainer component={Paper} variant="outlined">
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Started</TableCell>
								<TableCell>Workspace</TableCell>
								<TableCell>Agent</TableCell>
								<TableCell>Transport</TableCell>
								<TableCell>Number</TableCell>
								<TableCell>Status</TableCell>
								<TableCell align="right">Duration</TableCell>
								<TableCell align="right">Turns</TableCell>
								<TableCell align="right">Cut short</TableCell>
								<TableCell align="right">Silent</TableCell>
								<TableCell align="right">Worst backlog</TableCell>
								<TableCell>Preview</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{sessions.map((session) => (
								<TableRow
									key={session.id}
									hover
									onClick={() => navigate(`/sessions/${session.id}`)}
									sx={{ cursor: "pointer" }}
								>
									<TableCell sx={{ whiteSpace: "nowrap" }}>
										{formatEpoch(session.startedAt)}
									</TableCell>
									<TableCell>{teamName(session.teamId)}</TableCell>
									<TableCell>{session.agentName ?? "—"}</TableCell>
									<TableCell>
										{session.transport}
										{session.direction ? ` · ${session.direction}` : ""}
									</TableCell>
									<TableCell>{session.theirE164 ?? "—"}</TableCell>
									<TableCell>
										<Chip
											label={session.sessionStatus}
											size="small"
											color={session.sessionStatus === "failed" ? "error" : "default"}
										/>
									</TableCell>
									<TableCell align="right">{formatMs(session.durationMs)}</TableCell>
									<TableCell align="right">{session.turns}</TableCell>
									<TableCell align="right">{session.interruptedTurns}</TableCell>
									<TableCell
										align="right"
										sx={{ color: session.silentAgentTurns > 0 ? "error.main" : undefined }}
									>
										{session.silentAgentTurns}
									</TableCell>
									<TableCell align="right">{formatMs(session.worstCarrierBacklogMs)}</TableCell>
									<TableCell
										sx={{
											maxWidth: 260,
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
										}}
									>
										{session.transcriptPreview ?? ""}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			)}
		</PageShell>
	);
};
